<script setup lang="ts">
  import { computed, nextTick, onBeforeMount, onBeforeUnmount, ref } from 'vue';
  import { useRoute } from 'vue-router';

  import type { Call, Partner, Task } from '@stuut/api/models';
  import { CallStatus } from '@stuut/api/models';
  import { Shimmer } from '@ui/components/ai-elements/shimmer';
  import { AudioMiniPlayer } from '@ui/components/audio';
  import { Button } from '@ui/components/button';
  import { Card, CardContent, CardHeader, CardTitle } from '@ui/components/card';
  import { Separator } from '@ui/components/separator';
  import { Tabs, TabsList, TabsTrigger } from '@ui/components/tabs';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn, downloadFile } from '@ui/utils/utils';

  import { stuut } from '@/api';
  import CallDetails from '@/components/calls/CallDetails.vue';
  import CallTranscript from '@/components/calls/CallTranscript.vue';
  import TaskItem from '@/components/common/TaskItem.vue';
  import TaskReplyWarning from '@/components/tasks/TaskReplyWarning.vue';
  import { useTaskCollectionStore } from '@/store';
  import type { CallProcessingStep } from '@/types/callProcessing';
  import { getCallProcessingLabel } from '@/types/callProcessing';

  interface Props {
    call: Call;
    processingStep?: CallProcessingStep;
  }

  const props = defineProps<Props>();
  const emits = defineEmits<{
    (e: 'close'): void;
    (e: 'openTask', task: Task): void;
  }>();
  const currentTaskType = ref('open');
  const route = useRoute();

  const partner = ref<Partner>();
  const taskCollectionStore = useTaskCollectionStore();

  const currAudioTime = ref(0);
  const audioPlayer = ref();

  const isDownloading = ref(false);
  const isDownloaded = ref(false);

  const refreshInterval = ref();
  const numRefreshes = ref<number>(0);

  const filteredTasks = computed(() => {
    switch (currentTaskType.value) {
      case 'closed':
        return props.call?.closedTasks;
      case 'completed':
        return props.call?.completedTasks;
      case 'open':
      default:
        return props.call?.openTasks;
    }
  });

  onBeforeMount(async () => {
    await fetchCallData();
    if (
      props.call.status === CallStatus.REGISTERED ||
      props.call.status === CallStatus.INITIATED ||
      props.call.status === CallStatus.RINGING ||
      props.call.status === CallStatus.IN_PROGRESS ||
      props.call.status === CallStatus.AWAITING_POST_PROCESSING
    ) {
      refreshInterval.value = setInterval(() => {
        stuut.calls
          .retrieve(props.call.id, props.call)
          .then(() => {
            if (
              refreshInterval.value &&
              (numRefreshes.value > 150 ||
                props.call.status === CallStatus.FAILED ||
                props.call.status === CallStatus.CANCELED ||
                (props.call.end_time && props.call.transcript_str))
            ) {
              clearInterval(refreshInterval.value);
              refreshInterval.value = undefined;
            }
          })
          .catch(() => {
            if (refreshInterval.value) {
              clearInterval(refreshInterval.value);
              refreshInterval.value = undefined;
            }
          })
          .finally(() => ++numRefreshes.value);
      }, 1000 * 5);
    }
  });

  onBeforeUnmount(() => {
    if (refreshInterval.value) clearInterval(refreshInterval.value);
  });

  const fetchCallData = async () => {
    try {
      if (props.call.partner) {
        partner.value = await stuut.partners.retrieve(props.call.partner.id, props.call.partner);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const downloadRecording = () => {
    if (isDownloading.value || !props.call.recording_url) return;
    isDownloading.value = true;
    setTimeout(() => {
      if (props.call.recording_url) {
        downloadFile(props.call.recording_url, '');
      }
      isDownloading.value = false;
    }, 2000);
  };

  const isProcessing = computed(
    () => !!props.processingStep && props.processingStep !== 'completed',
  );

  const processingLabel = computed(() =>
    isProcessing.value && props.processingStep
      ? getCallProcessingLabel(props.processingStep)
      : undefined,
  );

  const handleTaskClick = (task: Task) => {
    emits('close');
    nextTick(() => {
      setTimeout(() => {
        taskCollectionStore.openTaskByQuery?.(task, route.name as string);
      }, 50);
    });
  };
</script>

<template>
  <div
    :class="
      cn('h-full overflow-y-auto p-6 grid gap-y-4 gap-x-6', call.transcript_str && 'lg:grid-cols-2')
    "
  >
    <div class="flex flex-col gap-4">
      <CallDetails :call="call" class="bg-background" />

      <Button
        v-if="processingLabel || (isProcessing && !call.tasks?.length)"
        variant="outline"
        class="w-full h-10 px-2.5 gap-x-2 justify-start"
      >
        <i class="fa-solid fa-sparkles text-agent text-2xs!" />
        <Shimmer as="span" :duration="1.5" class="text-xs">
          {{ processingLabel }}
        </Shimmer>
      </Button>

      <Card v-if="call.tasks?.length">
        <CardHeader>
          <CardTitle>Tasks</CardTitle>
        </CardHeader>

        <CardContent class="space-y-4">
          <Tabs v-model="currentTaskType">
            <TabsList no-border>
              <TabsTrigger value="open"
                >Open
                <span v-if="call.openTasks?.length" class="font-light">
                  {{ call.openTasks?.length }}
                </span>
              </TabsTrigger>

              <TabsTrigger value="completed"
                >Completed
                <span v-if="call.completedTasks?.length" class="font-light">
                  {{ call.completedTasks?.length }}
                </span>
              </TabsTrigger>

              <TabsTrigger value="closed"
                >Closed
                <span v-if="call.closedTasks?.length" class="font-light">
                  {{ call.closedTasks?.length }}
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div v-if="filteredTasks?.length" class="space-y-2.5">
            <Button
              v-for="task in filteredTasks"
              :key="task.id"
              variant="outline"
              class="w-full h-10 px-0 gap-x-0 group"
              :class="{
                'hover:bg-agent': task?.isReplyRequired && task?.isOpen,
                'hover:bg-muted/70': task?.isReplyRequired && !task?.isOpen,
              }"
              @click="emits('openTask', task)"
            >
              <TaskItem
                class="px-2.5 hover:bg-transparent [&>div]:h-full h-full rounded"
                :task="task"
                size="2xs"
                :truncate="true"
                :show-customer="false"
              >
                <template #task-item-content-right>
                  <div class="flex items-center gap-x-2">
                    <TaskReplyWarning
                      class="text-2xs text-muted-foreground"
                      gap-class-name="gap-x-0.5"
                      icon-class-name="text-3xs!"
                      :task="task"
                    />
                  </div>
                </template>
              </TaskItem>

              <Tooltip :delay-duration="0">
                <TooltipTrigger as-child>
                  <div
                    v-if="task?.isReplyRequired"
                    class="flex items-center gap-x-2 h-full border-l px-2.5"
                  >
                    <span class="flex items-center gap-x-1 text-agent">
                      <i class="fa-solid fa-sparkles text-agent text-2xs!"></i>
                      <span class="text-3xs">Reply with task</span>
                    </span>

                    <i class="fa far fa-reply text-agent text-2xs!" />
                  </div>

                  <div
                    v-else
                    class="flex items-center gap-x-2 h-full border-l px-2.5 text-muted-foreground"
                  >
                    <i class="fa far fa-arrow-right text-muted-foreground text-2xs!" />
                  </div>
                </TooltipTrigger>

                <TooltipContent side="top">
                  <template v-if="task?.isReplyRequired">
                    Click to view/edit and send the automatically generated response from task.
                  </template>

                  <template v-else> Click to view task. </template>
                </TooltipContent>
              </Tooltip>
            </Button>
          </div>

          <div v-else class="h-12 flex items-center border px-5 text-xs rounded">
            <p class="text-muted-foreground text-xs">No {{ currentTaskType }} tasks</p>
          </div>
        </CardContent>
      </Card>
    </div>

    <Card v-if="call.transcript_str">
      <CardHeader>
        <CardTitle> Transcript</CardTitle>
      </CardHeader>

      <CardContent class="flex flex-col gap-4">
        <div v-if="call.recording_url" class="flex gap-2 items-center">
          <AudioMiniPlayer
            ref="audioPlayer"
            :src="call.recording_url"
            class="flex-1"
            @timeupdate="(currTime) => (currAudioTime = currTime)"
          />

          <Button variant="ghost" size="icon" :disabled="isDownloading" @click="downloadRecording">
            <i
              class="fas"
              :class="
                isDownloading
                  ? 'fa-spinner-third animate-spin'
                  : isDownloaded
                    ? 'fa-check text-success'
                    : 'fa-download'
              "
            />
          </Button>
        </div>

        <Separator v-if="call.recording_url" />

        <CallTranscript
          :call
          :audio-time="currAudioTime"
          @jump-to="(time: number) => audioPlayer?.seekTo(time)"
        />
      </CardContent>
    </Card>
  </div>
</template>

<style scoped lang="postcss"></style>
